import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useGeri, useDuzenlemeBitti } from '@/lib/geri';
import api from '@/lib/api';
import { useProducts } from '@/contexts/ProductsContext';
import { birimSecenekleri, anaBirimKarsiligi } from '@/lib/birimler';
import ProductCombobox from '@/components/ProductCombobox';
import SearchableSelect from '@/components/SearchableSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Yeni sipariş VE taslak sipariş düzenleme.
 *
 * `/purchase-orders/new` yeni kayıt açar, `/purchase-orders/:id/edit` mevcut
 * taslağı yükleyip günceller. Gönderilmiş sipariş düzenlenemez.
 */
export default function PurchaseOrderNew() {
  const { products } = useProducts();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/purchasing');
  const duzenlemeBitti = useDuzenlemeBitti();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request_id');
  const { id } = useParams();
  const duzenleme = Boolean(id);

  const [suppliers, setSuppliers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [siparisNo, setSiparisNo] = useState('');
  const [productSuppliers, setProductSuppliers] = useState({}); // product_id -> [suppliers]

  const [form, setForm] = useState({
    supplier_id: '',
    purchase_request_id: requestId || '',
    expected_delivery_date: '',
    notes: '',
    items: []
  });

  useEffect(() => {
    fetchData();
    if (requestId) {
      loadRequestItems();
    }
  }, [requestId]);

  // Düzenleme: mevcut siparişi yükle
  useEffect(() => {
    if (!id) return;
    api.get(`/purchase-orders/${id}`)
      .then(r => {
        const s = r.data;
        if (s.status !== 'draft') {
          toast.error('Yalnızca gönderilmemiş siparişler düzenlenebilir');
          navigate(`/erp/purchase-orders/${id}`, { replace: true });
          return;
        }
        setSiparisNo(s.order_no || '');
        setForm({
          supplier_id: s.supplier_id || '',
          purchase_request_id: s.purchase_request_id || '',
          expected_delivery_date: s.expected_delivery_date || '',
          notes: s.notes || '',
          items: (s.items || []).map(i => ({
            product_card_id: i.product_card_id,
            product_name: i.product_name || '',
            quantity: String(i.quantity ?? ''),
            unit: i.unit || 'kg',
            unit_price: String(i.unit_price ?? ''),
            supplier_id: i.supplier_id || '',
          })),
        });
      })
      .catch(() => toast.error('Sipariş yüklenemedi'));
  }, [id, navigate]);

  const fetchData = async () => {
    try {
      // NOT: Burada eskiden `setProducts(...)` cagriliyordu ama boyle bir
      // setter yok — urunler ProductsContext'ten geliyor. Her acilista
      // ReferenceError firlatip "Veriler yuklenemedi" hatasi gosteriyordu.
      const [suppliersRes, unitsRes] = await Promise.all([
        api.get('/suppliers'),
        api.get('/units').catch(() => ({ data: [] })),
      ]);
      // `/suppliers` hesabin her PARA BIRIMI icin ayri satir dondurur
      // (bakiye ekranlari boyle istiyor). Secici icin id bazinda tekillestir;
      // aksi halde ayni tedarikci listede 4 kez cikiyor.
      const gorulen = new Set();
      setSuppliers((suppliersRes.data || []).filter(t => {
        if (!t?.id || gorulen.has(t.id)) return false;
        gorulen.add(t.id);
        return true;
      }));
      setUnits(unitsRes.data || []);
    } catch (err) {
      toast.error('Veriler yüklenemedi');
    }
  };

  const loadRequestItems = async () => {
    try {
      const res = await api.get(`/purchase-requests/${requestId}`);
      const request = res.data;
      const items = (request.items || []).map(item => ({
        product_card_id: item.product_card_id,
        product_name: item.product_name,
        quantity: item.requested_quantity?.toString() || '',
        unit: item.unit,
        unit_price: item.last_purchase_price?.toString() || '',
        // Oneri urun kartindan gelir (asagidaki etki doldurur), her kalem
        // icin ayri istek atilmaz
        supplier_id: '',
      }));
      setForm(prev => ({ ...prev, items, purchase_request_id: requestId }));
    } catch (err) {
      toast.error('Talep yüklenemedi');
    }
  };


  // Satır yüksekliği SABİT kalmalı: çevrim ipucu belirip kaybolduğunda
  // hücreler kayıyordu (miktar yazarken satır büyüyordu). İpucu satırı her
  // zaman ayrılır, boşken görünmez; satırdaki hücreler de üstten hizalanır
  // ([&>td]:align-top) ki bir hücredeki fazladan satır komşularını itmesin.
  // NOT: sınıf adı LITERAL yazılmalı — Tailwind kaynak metni tarar, şablon
  // içinde parçalanan sınıf üretilmez.
  const IPUCU = 'h-4 mt-0.5 text-[11px] leading-4 text-[#8a7e70] truncate';

  // Birim mantığı ortak: talep ekranı da aynı kaynağı kullanır (lib/birimler.js)
  const kalemBirimleri = (productId) =>
    birimSecenekleri(products.find(x => x.id === productId), units);
  const kalemCevrimi = (item) =>
    anaBirimKarsiligi(products.find(x => x.id === item.product_card_id), units, item.unit);

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { product_card_id: '', quantity: '', unit_price: '', unit: 'kg', supplier_id: '' }]
    }));
  };

  // Bir birimin ana birim karsiligi (1 <birim> = kac ana birim)
  const birimCarpani = (productId, birim) =>
    kalemBirimleri(productId).find(o => o.deger === birim)?.carpan ?? 1;

  // Kayan nokta artiklarini temizle: 130 * 0.2 = 26.000000000000004
  const yuvarla = (n) => +(Math.round(n * 1e6) / 1e6);

  const updateItem = (i, field, value) => {
    const items = [...form.items];

    // BIRIM DEGISTIYSE FIYATI DA CEVIR.
    // Fiyat alani SECILI birim basinadir. Cevrilmezse kg fiyati adet
    // fiyatiymis gibi kaliyor ve alttaki ipucu sacma bir deger gosteriyordu:
    // 130 TL/kg iken "adet" secilince 130 / 0.2 = 650 TL/kg.
    if (field === 'unit' && value !== items[i].unit) {
      const eskiCarpan = birimCarpani(items[i].product_card_id, items[i].unit);
      const yeniCarpan = birimCarpani(items[i].product_card_id, value);
      const fiyat = parseFloat(items[i].unit_price);
      if (fiyat && eskiCarpan && yeniCarpan) {
        // once ANA birime don, sonra yeni birime cevir
        items[i].unit_price = String(yuvarla((fiyat / eskiCarpan) * yeniCarpan));
      }
    }

    items[i][field] = value;
    if (field === 'product_card_id') {
      const p = products.find(x => x.id === value);
      if (p) {
        items[i].unit = p.unit;
        items[i].product_name = p.name;
        if (p.last_purchase_price) items[i].unit_price = p.last_purchase_price.toString();
        // Son alinan tedarikciyi oner (karttan, ek istek yok)
        if (!items[i].supplier_id && p.last_purchase_supplier_id) {
          items[i].supplier_id = p.last_purchase_supplier_id;
          if (p.last_purchase_supplier_price) {
            items[i].unit_price = p.last_purchase_supplier_price.toString();
          }
        }
      }
    }
    setForm(prev => ({ ...prev, items }));
  };

  // Urunun EN SON alindigi tedarikci URUN KARTINDA yazili
  // (`last_purchase_supplier_id`, alim aninda yazilir). Kartlar zaten
  // ProductsContext ile gelmis durumda; oneri SIFIR ek istekle cozuluyor.
  // Eskiden her kalem icin `/products/{id}/suppliers` cagriliyordu.
  const onerilenTedarikci = (productId) =>
    products.find(x => x.id === productId)?.last_purchase_supplier_id || '';

  // Talepten gelen kalemlerde oneriyi kartlar YUKLENINCE doldur.
  // Kartlar ProductsContext ile asenkron geliyor; talep ekrandan once
  // yuklenirse oneri bos kaliyordu.
  useEffect(() => {
    if (!products.length || !form.items.length) return;
    setForm(prev => {
      let degisti = false;
      const items = prev.items.map(it => {
        if (it.supplier_id || !it.product_card_id) return it;
        const p = products.find(x => x.id === it.product_card_id);
        if (!p?.last_purchase_supplier_id) return it;
        degisti = true;
        return {
          ...it,
          supplier_id: p.last_purchase_supplier_id,
          unit_price: it.unit_price || (p.last_purchase_supplier_price ?? '').toString(),
        };
      });
      return degisti ? { ...prev, items } : prev;
    });
  }, [products, form.items.length]);

  // Tam alim gecmisi yalnizca kullanici acilir listeyi ACTIGINDA cekilir.
  const gecmisiGetir = async (productId) => {
    if (!productId || productSuppliers[productId]) return;
    try {
      const res = await api.get(`/products/${productId}/suppliers`);
      setProductSuppliers(prev => ({ ...prev, [productId]: res.data || [] }));
    } catch (err) {
      setProductSuppliers(prev => ({ ...prev, [productId]: [] }));
    }
  };

  const removeItem = (i) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, j) => j !== i) }));
  };

  // Secici seceneklerini kur: onerilen -> daha once alinan -> digerleri
  const tedarikciSecenekleri = (productId) => {
    const gecmis = productSuppliers[productId] || [];
    const onerilenId = onerilenTedarikci(productId);
    const kullanilan = new Set();
    const secenekler = [];

    const kart = products.find(x => x.id === productId);
    if (onerilenId) {
      const t = suppliers.find(x => x.id === onerilenId);
      if (t) {
        kullanilan.add(t.id);
        const tarih = (kart?.last_purchase_supplier_date || '').slice(0, 10);
        const fiyat = kart?.last_purchase_supplier_price;
        secenekler.push({
          value: t.id, label: t.name, group: 'Son Alınan',
          hint: [fiyat ? `${Number(fiyat).toFixed(2)} TRY` : null, tarih].filter(Boolean).join(' · '),
        });
      }
    }
    gecmis.forEach(t => {
      if (kullanilan.has(t.id)) return;
      kullanilan.add(t.id);
      secenekler.push({
        value: t.id, label: t.name, group: 'Daha Önce Alınan',
        hint: [t.last_price ? `${Number(t.last_price).toFixed(2)} TRY` : null,
               (t.last_purchase_date || '').slice(0, 10)].filter(Boolean).join(' · '),
      });
    });
    suppliers.forEach(t => {
      if (kullanilan.has(t.id)) return;
      kullanilan.add(t.id);
      secenekler.push({ value: t.id, label: t.name, group: 'Diğer Tedarikçiler' });
    });
    return secenekler;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) {
      toast.error('En az bir ürün ekleyin');
      return;
    }
    // Check all items have supplier
    const hasAllSuppliers = form.items.every(i => i.supplier_id || form.supplier_id);
    if (!hasAllSuppliers && !form.supplier_id) {
      toast.error('Her kalem için tedarikçi seçin veya genel tedarikçi belirleyin');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: form.items.map(i => ({
          product_card_id: i.product_card_id,
          product_name: i.product_name || '',
          quantity: parseFloat(i.quantity),
          unit: i.unit,
          unit_price: parseFloat(i.unit_price),
          supplier_id: i.supplier_id || form.supplier_id
        }))
      };
      if (duzenleme) {
        await api.patch(`/purchase-orders/${id}`, payload);
        toast.success('Sipariş güncellendi');
        duzenlemeBitti(`/erp/purchase-orders/${id}`);
      } else {
        await api.post('/purchase-orders', payload);
        toast.success('Sipariş oluşturuldu');
        navigate('/erp/purchasing');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
    setLoading(false);
  };

  const total = form.items.reduce((sum, i) => sum + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="purchase-order-new-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]">
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916]">
            {duzenleme ? 'Siparişi Düzenle' : 'Yeni Sipariş'}
          </h1>
          {duzenleme && siparisNo && <p className="text-sm text-[#8a7e70]">{siparisNo}</p>}
          {!duzenleme && requestId && <p className="text-sm text-[#8a7e70]">Talepten oluşturuluyor</p>}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Info */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Sipariş Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Genel Tedarikçi (Opsiyonel)</Label>
              <SearchableSelect
                value={form.supplier_id}
                options={suppliers.map(t => ({ value: t.id, label: t.name, hint: t.code || '' }))}
                onChange={(v) => setForm(p => ({ ...p, supplier_id: v }))}
                placeholder="Tedarikçi seçin (tüm kalemler için)"
                searchPlaceholder="Tedarikçi ara..."
                emptyText="Tedarikçi bulunamadı"
                testId="order-supplier-select"
              />
              <p className="text-xs text-[#8a7e70]">Her kalem için ayrı tedarikçi de seçebilirsiniz</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Beklenen Teslim Tarihi</Label>
              <Input 
                type="date" 
                value={form.expected_delivery_date}
                onChange={(e) => setForm(p => ({ ...p, expected_delivery_date: e.target.value }))}
                className="bg-white border-[#d0c4b0]" 
                data-testid="order-delivery-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Notlar</Label>
              <Textarea 
                value={form.notes}
                onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                className="bg-white border-[#d0c4b0] h-10" 
                placeholder="Sipariş notları..."
                data-testid="order-notes"
              />
            </div>
          </CardContent>
        </Card>

        {/* Items */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Sipariş Kalemleri</CardTitle>
            <Button type="button" onClick={addItem} size="sm" className="bg-[#2e2d29] text-[#f5f0e8]" data-testid="order-add-item">
              <Plus size={14} className="mr-1" /> Kalem Ekle
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold">Ürün</TableHead>
                  <TableHead className="text-[#1a1916] font-bold">Tedarikçi</TableHead>
                  <TableHead className="text-[#1a1916] font-bold w-24">Miktar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold w-20">Birim</TableHead>
                  <TableHead className="text-[#1a1916] font-bold w-28">Birim Fiyat</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-right">Tutar</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.items.map((item, i) => {
                  const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
                  // Yalnizca DONUSUMU OLAN urunlerde ipucu yuvasi ayrilir;
                  // donusumu olmayan satirlar eskisi gibi kompakt kalir.
                  const cevrimliUrun = kalemBirimleri(item.product_card_id).length > 1;
                  
                  return (
                    <TableRow key={i} className="border-[#d0c4b0] [&>td]:align-top">
                      <TableCell>
                        {/* Düz açılır listede binlerce ürün arasında kaydırmak
                            gerekiyordu; ProductCombobox ad ve koda göre,
                            Türkçe karakter duyarlı arar. */}
                        <ProductCombobox
                          value={item.product_card_id}
                          products={products}
                          onChange={(v) => updateItem(i, 'product_card_id', v)}
                          placeholder="Ürün seçin"
                          testId={`order-item-product-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        {/* Öneri (Son Alınan) ürün kartından bedavaya gelir;
                            tam alım geçmişi liste AÇILDIĞINDA çekilir. */}
                        <SearchableSelect
                          value={item.supplier_id}
                          options={tedarikciSecenekleri(item.product_card_id)}
                          onChange={(v) => updateItem(i, 'supplier_id', v)}
                          onOpen={() => gecmisiGetir(item.product_card_id)}
                          placeholder="Tedarikçi"
                          searchPlaceholder="Tedarikçi ara..."
                          emptyText="Tedarikçi bulunamadı"
                          testId={`order-item-supplier-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number" 
                          step="0.01" 
                          value={item.quantity}
                          onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                          className="bg-white border-[#d0c4b0]" 
                          data-testid={`order-item-qty-${i}`}
                        />
                        {cevrimliUrun && (
                        <div className={IPUCU} data-testid={`order-item-base-${i}`}>
                          {(() => {
                            // Alternatif birim seçildiyse stoğa girecek ana
                            // birim karşılığı — "3 koli → 18 kg"
                            const k = kalemCevrimi(item);
                            const m = parseFloat(item.quantity);
                            if (!k || !m) return null;
                            return `stok: ${+(m * k.carpan).toFixed(4)} ${k.ana}`;
                          })()}
                        </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const secenekler = kalemBirimleri(item.product_card_id);
                          if (secenekler.length <= 1) {
                            // Seçim kutusuyla AYNI yüksekliği kapla; aksi halde
                            // dönüşümü olan/olmayan satırlar farklı yükseklikte
                            return (
                              <span className="flex h-9 items-center text-sm text-[#8a7e70]">
                                {item.unit || '-'}
                              </span>
                            );
                          }
                          return (
                            <select
                              value={item.unit || ''}
                              onChange={(e) => updateItem(i, 'unit', e.target.value)}
                              title="Ürün kartında tanımlı birimlerden seçin"
                              className="h-9 w-full min-w-[92px] px-1.5 rounded-md border border-[#d0c4b0] bg-white text-sm focus:outline-none focus:border-[#3d6e4e]"
                              data-testid={`order-item-unit-${i}`}>
                              {secenekler.map(o => (
                                <option key={o.deger} value={o.deger}>{o.etiket}</option>
                              ))}
                            </select>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number" 
                          step="0.01" 
                          value={item.unit_price}
                          onChange={(e) => updateItem(i, 'unit_price', e.target.value)}
                          className="bg-white border-[#d0c4b0]" 
                          data-testid={`order-item-price-${i}`}
                        />
                        {cevrimliUrun && (
                        <div className={IPUCU}>
                          {(() => {
                            // Fiyat SEÇİLİ birim başınadır; maliyet ana birime çevrilir
                            const k = kalemCevrimi(item);
                            const f = parseFloat(item.unit_price);
                            if (!k || !f) return null;
                            return `${+(f / k.carpan).toFixed(4)} TRY/${k.ana}`;
                          })()}
                        </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono font-semibold text-[#1a1916]">
                        <div className="flex h-9 items-center justify-end">
                          {lineTotal.toFixed(2)} TRY
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeItem(i)}
                          className="text-[#7a3535] hover:bg-[#7a3535]/10"
                          data-testid={`order-remove-${i}`}
                        >
                          <X size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {form.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-[#8a7e70]">
                      Sipariş kalemi ekleyin
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="flex items-center justify-between bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-4">
          <p className="text-sm text-[#8a7e70]">
            Toplam: <span className="font-bold text-[#1a1916] text-xl">{total.toFixed(2)} TRY</span>
          </p>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={geri} className="border-[#d0c4b0]">
              İptal
            </Button>
            <Button 
              type="submit" 
              disabled={loading} 
              className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]"
              data-testid="save-order-btn"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              {duzenleme ? 'Siparişi Güncelle' : 'Sipariş Oluştur'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
